import { useMutation, useQuery } from "@apollo/client";
import {
  ActionIcon,
  Button,
  Grid,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { CREATE_USER } from "apollo/mutuations";
import { GET_ALL_USERS, GET_ROLES } from "apollo/queries";
import ImageUploader from "components/controls/ImageUpload";
import { customLoader } from "components/utilities/loader";
import { useRef, useState } from "react";

const UserAddModal = ({
  setOpened,
  total,
  setTotal,
  activePage,
  size,
  setActivePage,
  roleId,
}) => {
  // roles
  const [roles, setRoles] = useState([]);
  const [files, setFiles] = useState([]);
  const [visible, setVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  // ref for file input
  const fileInputRef = useRef(null);

  // apollo queries
  const { data: rolesList, loading: rolesListLoading } = useQuery(GET_ROLES, {
    onCompleted(data) {
      let roles = data.roles;
      let rolesArray = [];

      roles.forEach((role) => {
        rolesArray.push({
          label: role.name,
          value: role.name,
        });
      });

      setRoles([...rolesArray]);
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: err?.graphQLErrors?.[0]?.message || "Failed to Create User",
      });
    },
  });

  const [addUser, { loading: userLoading }] = useMutation(CREATE_USER, {
    update(cache, { data: { createUser } }) {
      cache.updateQuery(
        {
          query: GET_ALL_USERS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: "",
            role: roleId,
          },
        },
        (data) => {
          if (data) {
            return {
              users: {
                ...data.users,
                data: [createUser, ...data.users.data],
              },
            };
          }
        },
      );
    },
  });

  // form state
  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      password: "",
      phone: "",
      password_confirmation: "",
      role: "",
    },
    validate: {
      phone: (value) =>
        value.length > 13 ? "Phone number must be exactly 13 characters" : null,
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(value))
          return "Password must contain at least one uppercase letter";
        if (!/[0-9]/.test(value))
          return "Password must contain at least one number";
        if (!/[^A-Za-z0-9]/.test(value))
          return "Password must contain at least one special character";
        return null;
      },
      password_confirmation: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });
  const submit = () => {
    addUser({
      variables: {
        name: form.getInputProps("name").value,
        email: form.getInputProps("email").value,
        password: form.getInputProps("password").value,
        phone: form.getInputProps("phone").value,
        password_confirmation: form.getInputProps("password_confirmation")
          .value,
        profile_image: files[files.length - 1],
        role: form.getInputProps("role").value,
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "User Created Successfully",
        });

        setOpened(false);
      },
      onError(error) {
        setOpened(true);

        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Create User",
        });
      },
    });
  };

  // handle file change
  const handleFileChange = (files) => {
    setFiles(files);
  };

  const { height } = useViewportSize();

  const setRoleDropDownValue = (val) => {
    form.setFieldValue("role", val);
  };

  return (
    <>
      <LoadingOverlay
        visible={rolesListLoading || userLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <div>
        <ScrollArea
          style={{ height: height / 1.2 }}
          type="auto"
          offsetScrollbars
        >
          <form onSubmit={form.onSubmit(() => submit())}>
            <Stack>
              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    required
                    label="Name"
                    placeholder="Name"
                    {...form.getInputProps("name")}
                  />
                  <TextInput
                    required
                    label="Email"
                    placeholder="Email"
                    type="email"
                    {...form.getInputProps("email")}
                  />
                  <TextInput
                    required
                    label="Phone"
                    placeholder="Phone"
                    maxLength={13}
                    {...form.getInputProps("phone")}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9+]/g, "");
                    }}
                  />
                  <TextInput
                    required
                    label="Password"
                    placeholder="Password"
                    type={visible ? "text" : "password"}
                    {...form.getInputProps("password")}
                    rightSection={
                      <ActionIcon
                        onClick={() => setVisible((v) => !v)}
                        variant="transparent"
                      >
                        {visible ? (
                          <IconEyeOff size={16} />
                        ) : (
                          <IconEye size={16} />
                        )}
                      </ActionIcon>
                    }
                  />

                  <TextInput
                    required
                    label="Password Confirmation"
                    placeholder="Password Confirmation"
                    type={confirmVisible ? "text" : "password"}
                    {...form.getInputProps("password_confirmation")}
                    rightSection={
                      <ActionIcon
                        onClick={() => setConfirmVisible((v) => !v)}
                        variant="transparent"
                      >
                        {confirmVisible ? (
                          <IconEyeOff size={16} />
                        ) : (
                          <IconEye size={16} />
                        )}
                      </ActionIcon>
                    }
                  />

                  <Select
                    data={roles}
                    value={form.getInputProps("role.connect")?.value}
                    onChange={setRoleDropDownValue}
                    label="Select Role"
                    placeholder="Pick a role this user belongs to"
                  />
                </Grid.Col>
              </Grid>

              <ImageUploader
                previewsPerRow={4}
                buttonLabel="Profile Image"
                onChange={handleFileChange}
              />

              <Grid>
                <Grid.Col span={12}>
                  <Button
                    style={{
                      width: "30%",

                      backgroundColor: "#FF6A00",
                      color: "#FFFFFF",
                    }}
                    fullWidth
                    type="submit"
                    color="blue"
                  >
                    Submit
                  </Button>
                </Grid.Col>
              </Grid>
            </Stack>
          </form>
        </ScrollArea>
      </div>
    </>
  );
};

export default UserAddModal;
